import { isCollectibleBasedIdentification, isLegoMinifigIdentification, type AiIdentification, type AiIdentification_LegoMinifigure } from "@/services/aiIdentification";
import type { CollectibleElastic } from "@/services/collectibles/elastic";
import { CategoryId } from "@/services/collection/categories";
import { createNumericPrice, type PriceNumeric } from "@/services/prices";
import type { Ref } from "vue";

type PriceList = {
  name: string | null;
  price: PriceNumeric;
}[];

export function useAiIdentificationPrice(identification: Ref<AiIdentification>) {
  /**
   * For some categories, we don't want to expose the price.
   * We also do not want to expose price for old Pokemon identifications.
   **/
  const isVisible = computed(() => {
    const categoryId = identification.value.categoryId;

    if (categoryId === CategoryId.LegoMinifigures) {
      return true;
    }

    if (categoryId === CategoryId.Pokemon && isCollectibleBasedIdentification(identification.value)) {
      return true;
    }

    return false;
  });

  const prices = computed((): PriceList => {
    if (!isVisible.value) {
      return [];
    }

    if (isLegoMinifigIdentification(identification.value)) {
      return extractPriceFromMinifig(identification.value);
    } else if (isCollectibleBasedIdentification(identification.value)) {
      return extractPriceFromCollectible(identification.value.collectible);
    } else {
      return [];
    }
  });

  return {
    isVisible,
    prices,
  };
}

function extractPriceFromMinifig(minifig: AiIdentification_LegoMinifigure): PriceList {
  if (minifig.priceFrom === null) {
    return [];
  } else {
    return [
      {
        name: null,
        price: createNumericPrice(minifig.priceFrom.price, minifig.priceFrom.currencyId),
      },
    ];
  }
}

function extractPriceFromCollectible(collectible: CollectibleElastic): PriceList {
  return collectible.prices.map((price) => ({
    name: price.type,
    price: createNumericPrice(price.price, price.currencyId),
  }));
}
